import { injectable } from "inversify";
import { BrowserFeatureDetector } from "../../../utils/BrowserFeatureDetector";

export interface MusicPlayer {
  readonly isPlaying: boolean;
  src: string;
  volume: number;
  loop: boolean;
  playbackRate: number;
  currentTime: number;
  play(): void;
  pause(): void;
}

@injectable()
export class MusicPlayerImpl implements MusicPlayer {
  private static SILENT_WAV = "data:audio/wav;base64,UklGRjIAAABXQVZFZm10IBIAAAABAAEAQB8AAEAfAAABAAgAAABmYWN0BAAAAAAAAABkYXRhAAAAAA==";

  context: AudioContext;

  // We use it to initialize player on safari ( so src it's not empty )

  private shouldBePlaying: boolean;
  private safariInitialized: boolean;

  private readonly audio: HTMLAudioElement;

  // The reason for using gainNode is that because volume transitions
  // won't work on Safari without it, because you can't modify audio's volume directly.
  private gainNode: GainNode;
  private volumeValue: number;

  constructor() {
    this.audio = new Audio();
    this.audio.crossOrigin = "anonymous";
    this.audio.volume = 1;

    if (BrowserFeatureDetector.isSafari()) {
      this.audio.src = MusicPlayerImpl.SILENT_WAV;
    }

    const initializeOnUserInteraction = async () => {
      window.removeEventListener("click", initializeOnUserInteraction);

      this.gainNode = this.createConnectedGainNode();
      this.volume = this.volumeValue;

      if (BrowserFeatureDetector.isSafari()) {
        await this.initializeSafari();
      }
    };

    window.addEventListener("click", initializeOnUserInteraction);

    this.loop = false;

    // @ts-ignore
    window.AUDIO = this;
  }

  get playbackRate(): number {
    return this.audio.playbackRate;
  }

  set playbackRate(playbackRate: number) {
    this.audio.playbackRate = playbackRate;
  }

  get isPlaying(): boolean {
    return !this.audio.paused;
  }

  get currentTime(): number {
    return this.audio.currentTime;
  }

  set currentTime(time: number) {
    this.audio.currentTime = time;
  }

  get volume(): number {
    return this.volumeValue;
  }

  set volume(volume: number) {
    this.volumeValue = volume;

    if (this.gainNode) {
      this.gainNode.gain.value = this.volumeValue;
    }
  }

  get loop(): boolean {
    return this.audio.loop;
  }

  set loop(loop: boolean) {
    this.audio.loop = loop;
  }

  set src(src: string) {
    this.audio.src = src;
    this.audio.load();
  }

  get src(): string {
    return this.audio.src;
  }
  play() {
    this.shouldBePlaying = true;
    this.update();
  }

  pause(): void {
    this.shouldBePlaying = false;
    this.update();
  }

  private update() {
    if (this.shouldBePlaying) {
      if (BrowserFeatureDetector.isSafari() && !this.safariInitialized && !this.audio.paused) {
        this.audio.pause();
      }

      if (!this.audio.ended || this.loop) {
        this.audio.play().then(() => {});
      }
    } else {
      this.audio.pause();
    }
  }

  /**
   * Safari browsers require the first audio play to be a result of user interaction
   */
  private async initializeSafari() {
    this.gainNode.gain.value = 0;

    await this.audio.play();

    this.safariInitialized = true;
    this.update();

    this.gainNode.gain.value = this.volumeValue;
  }

  private createConnectedGainNode() {
    // @ts-ignore
    const context: AudioContext = new (window.AudioContext || window.webkitAudioContext)();

    this.context = context;
    const gainNode = context.createGain();

    const source = context.createMediaElementSource(this.audio);
    gainNode.connect(context.destination);
    source.connect(gainNode);

    return gainNode;
  }
}
